import { createFileRoute } from "@tanstack/react-router";

import ArtworkGallery from "@/components/marketing/artwork/ArtworkGallery";
import ArtworkWorkflow from "@/components/marketing/artwork/ArtworkWorkflow";
import FreeOfferBand from "@/components/marketing/artwork/FreeOfferBand";
import StyleComparison from "@/components/marketing/artwork/StyleComparison";
import { BrandLockup, HeroSubtitle } from "@/components/marketing/BrandLockup";
import FaqSection from "@/components/marketing/FaqSection";
import HowItWorks from "@/components/marketing/HowItWorks";
import { marketingHead } from "@/components/marketing/marketingHead";
import ProductHero, { ProductShot } from "@/components/marketing/ProductHero";
import RelatedProducts from "@/components/marketing/RelatedProducts";
import Footer from "@/components/nav/Footer";

const LEDE =
  "Create eye-popping cover art that captures the sound, mood, and identity of your music and helps it stand out from the crowd.";

export const Route = createFileRoute("/cover-art")({
  component: CoverArtPage,
  head: () =>
    marketingHead({
      title: "Create Cover Art Online | Lutely",
      description: LEDE,
      path: "/cover-art",
    }),
});

const FAQ = [
  {
    question: "Can I upload my own cover art?",
    answer:
      "Yes. You can upload your own artwork and use it on your tracks and releases.",
  },
  {
    question: "Can I add my artist name or release title?",
    answer:
      "Not yet. Lutely doesn’t currently offer tools to add text or design elements on top of your artwork.",
  },
  {
    question: "What size and resolution is the artwork?",
    answer:
      "Previews are 500 × 500 pixels. When you need to export, you can download a high-resolution version in different formats.",
  },
  {
    question: "Can I download my artwork and use it outside Lutely?",
    answer:
      "Yes. Download your artwork and use it wherever your music needs to go.",
  },
];

function CoverArtPage() {
  return (
    <main className="flex flex-col bg-white">
      <ProductHero
        title={
          <>
            <BrandLockup>Cover Art</BrandLockup>
            <br />
            <HeroSubtitle>Bring your vision to life.</HeroSubtitle>
          </>
        }
        lede={LEDE}
        cta="Create Artwork"
        curveClassName="after:bg-[#F6F7FB]"
        stage={
          <ProductShot
            tall
            src="/marketing/artwork/artwork-creator.webp"
            alt="Lutely AI artwork creator"
            caption="Real Lutely product screen"
          />
        }
      />
      <ArtworkGallery />
      <StyleComparison />
      <HowItWorks
        title="Go from an idea to release artwork."
        steps={[
          {
            title: "Describe your idea",
            body: "Tell Lutely the mood, colors, imagery, or direction you have in mind. Or try Surprise Me for a starting point.",
          },
          {
            title: "Explore the possibilities",
            body: "Try your prompt in different artistic styles without rewriting it. Compare the results, or adjust your idea to explore a new direction.",
          },
          {
            title: "Use it on your release",
            body: "Save your chosen artwork to your track and use it on your single or album.",
          },
        ]}
      />
      <ArtworkWorkflow />
      <FreeOfferBand />
      <FaqSection items={FAQ} />
      <RelatedProducts current="artwork" />
      <Footer />
    </main>
  );
}
